import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Spinner } from '../../components/ui/Spinner';
import { DAY_LABELS } from '../../lib/appointments/availability';
import type { CalendarAvailability, NoOfferReason } from '../../types/appointments';
import { getCalendarAvailability } from './api';

// What this calendar would offer right now — a real free/busy read run through
// the slot engine.
//
// ⚠️⚠️ IT SAYS *WHY* IT IS OFFERING NOTHING, and the two cases that matter most
// look identical without it. "Every connected calendar failed to read" and "the
// fortnight is genuinely full" both produce an empty list; one is a revoked share
// nobody has noticed, the other is a busy dealership. Collapsing them into "no
// availability" is how the first one goes unfixed until a customer is turned away.

/** ⚠️ Deliberately DIFFERENT words per reason — see the note above. */
export function describeNoOffer(reason: NoOfferReason): { title: string; detail: string } {
  switch (reason) {
    case 'disabled':
      return {
        title: 'This calendar is turned off',
        detail: 'Nothing will be offered from it until it is switched back on.',
      };
    case 'no-staff':
      return {
        title: 'Nobody is connected yet',
        detail: 'Connect at least one salesperson under Staff before this calendar can offer a time.',
      };
    case 'all-unreadable':
      return {
        title: 'We cannot read any of these calendars',
        detail:
          'Every connected calendar failed to read, so we do not know when anyone is free. Nothing will be offered and every booking request will go to a person — which is deliberate: guessing would mean booking over someone. Use Test connection on each staff member to find out why.',
      };
    case 'bad-timezone':
      return {
        title: 'The timezone is not one we recognise',
        detail:
          'Pick a timezone in Basic details. Until then no time can be offered, because we cannot work out what the opening hours mean.',
      };
    case 'fully-booked':
    default:
      return {
        title: 'Nothing free in this window',
        detail:
          'The calendars read fine — there is genuinely nothing available inside the opening hours, buffers and limits set below.',
      };
  }
}

function groupByDay(availability: CalendarAvailability) {
  const byDate = new Map<string, string[]>();
  for (const slot of availability.slots) {
    const list = byDate.get(slot.localDate) ?? [];
    list.push(slot.localStart.slice(11));
    byDate.set(slot.localDate, list);
  }
  return [...byDate.entries()];
}

function dayLabel(date: string): string {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!parts) return date;
  const weekday = new Date(
    Date.UTC(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3])),
  ).getUTCDay();
  return `${DAY_LABELS[weekday]} ${parts[3]}/${parts[2]}`;
}

export function AvailabilityPreview({ calendarId }: { calendarId: string }) {
  const { data, isLoading, isFetching, refetch, error } = useQuery({
    queryKey: ['booking-availability', calendarId],
    queryFn: () => getCalendarAvailability(calendarId),
    // ⚠️ NOT auto-refetching on a timer. This is a real Google request per load,
    // and a settings page left open should not keep making them.
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  if (isLoading) return <Spinner />;
  if (error) {
    return (
      <p className="text-sm text-rose-700">
        {(error as Error).message}
      </p>
    );
  }
  if (!data) return null;

  const days = groupByDay(data);
  const reason = data.noOfferReason ? describeNoOffer(data.noOfferReason) : null;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {data.slots.length > 0
            ? `${data.slots.length} times available between ${dayLabel(data.fromLocalDate)} and ${dayLabel(data.toLocalDate)}, in ${data.timezone}.`
            : `Checked ${dayLabel(data.fromLocalDate)} to ${dayLabel(data.toLocalDate)} in ${data.timezone}.`}
        </p>
        <Button type="button" variant="outline" size="sm" onClick={() => void refetch()}>
          {isFetching ? (
            <Spinner className="mr-1 h-3 w-3" />
          ) : (
            <RefreshCw className="mr-1 h-3.5 w-3.5" />
          )}
          Check again
        </Button>
      </div>

      {reason && (
        <div
          className={
            data.noOfferReason === 'all-unreadable' || data.noOfferReason === 'bad-timezone'
              ? 'rounded-md border border-rose-200 bg-rose-50 p-3'
              : 'rounded-md border border-slate-200 bg-slate-50 p-3'
          }
        >
          <p className="text-sm font-medium">{reason.title}</p>
          <p className="mt-1 text-xs text-muted-foreground">{reason.detail}</p>
        </div>
      )}

      {/* Anytime Fitness calendars: why AF could not be read, or that its response drifted (reported, never a crash). */}
      {data.providerNote && (
        <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="provider-note">
          {data.providerNote}
        </p>
      )}

      {/* ⚠️ Reported even when there ARE slots. One unreadable calendar out of
          three still means that salesperson is silently getting no bookings, and
          the list would otherwise look perfectly healthy. */}
      {data.unreadableMembers.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
          <p className="flex items-center gap-1.5 text-sm font-medium text-amber-900">
            <AlertTriangle className="h-4 w-4" />
            {data.unreadableMembers.length === 1
              ? 'One calendar could not be read'
              : `${data.unreadableMembers.length} calendars could not be read`}
          </p>
          <ul className="mt-1.5 space-y-1">
            {data.unreadableMembers.map((m) => (
              <li key={m.memberId} className="text-xs text-amber-800">
                <span className="font-medium">{m.displayName}</span>
                {m.googleCalendarId ? ` (${m.googleCalendarId})` : ''} — {m.error}
              </li>
            ))}
          </ul>
          <p className="mt-1.5 text-xs text-amber-800">
            They will not be offered for any booking until this is fixed.
          </p>
        </div>
      )}

      {days.length > 0 && (
        <div className="space-y-1.5">
          {days.map(([date, times]) => (
            <div key={date} className="flex flex-wrap items-baseline gap-2 text-sm">
              <span className="w-32 shrink-0 font-medium">{dayLabel(date)}</span>
              <span className="text-muted-foreground">{times.join(' · ')}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
